import Swal, { SweetAlertPosition } from "sweetalert2"
import { errorMessages, infoMessages, warningMessages } from "../enums/messages.enum"

export const successToaster = (text: string, position: SweetAlertPosition = 'top-right') => {
    Swal.fire({
        text,
        icon: 'success',
        background: 'var(--white-constant)',
        color: 'var(--black-constant)',
        confirmButtonColor: 'var(--primary)',
        showConfirmButton: false,
        toast: true,
        timerProgressBar: true,
        position,
        timer: 3000,
    })
}

export const warningToaster = (text: string, position: SweetAlertPosition = 'top-right'): void => {
    Swal.fire({
        text,
        icon: 'warning',
        background: 'var(--white-constant)',
        color: 'var(--black-constant)',
        confirmButtonColor: 'var(--primary)',
        showConfirmButton: false,
        toast: true,
        timerProgressBar: true,
        position,
        timer: 3000,
    })
}

export const persistentWarningToaster = (
    text: string,
    position: SweetAlertPosition = 'top',
) => {
    if (Swal.isVisible()) {
        Swal.update({ text });
        return;
    }

    Swal.fire({
        text,
        icon: 'warning',
        background: 'var(--white-constant)',
        color: 'var(--black-constant)',
        confirmButtonColor: 'var(--primary)',
        showConfirmButton: false,
        showCloseButton: true,
        toast: true,
        timerProgressBar: false,
        position,
    });
};

export const closePersistentWarningToaster = () => {
    if (Swal.isVisible()) {
        Swal.close();
    }
};

export const errorToaster = (text: string, position: SweetAlertPosition = 'top-right') => {
    Swal.fire({
        text: text ?? errorMessages.somethingWentWrong,
        icon: 'error',
        background: 'var(--white-constant)',
        color: 'var(--black-constant)',
        confirmButtonColor: 'var(--primary)',
        showConfirmButton: false,
        toast: true,
        timerProgressBar: true,
        position,
        timer: 3000,
    })
}

export const errorToasterAutoClose = (title: string, position: SweetAlertPosition = 'top-right') => {
    Swal.fire({
        title,
        icon: 'error',
        background: 'var(--white-constant)',
        color: 'var(--black-text)',
        confirmButtonColor: 'var(--primary)',
        showConfirmButton: false,
        toast: true,
        timerProgressBar: true,
        timer: 5000,
        position
    })
}

export const confirmationPopup = async (title: string = warningMessages.confirmationDefaultMsg, text?: string) => {
    return Swal.fire({
        title,
        icon: 'question',
        background: 'var(--alert-popup-bg)',
        color: 'var(--black-text)',
        showCancelButton: true,
        confirmButtonColor: 'var(--primary)',
        cancelButtonColor: 'var(--reset-button-bg)',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
    });
}
export const priorDownloadConfirmationPopup = async (title: string = 'Are you sure you want to download?', text?: string) => {
    return Swal.fire({
        title,
        icon: 'question',
        background: 'var(--alert-popup-bg)',
        color: 'var(--black-text)',
        showCancelButton: true,
        confirmButtonColor: 'var(--primary)',
        cancelButtonColor: 'var(--reset-button-bg)',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
    });
}
export const customConfirmationPopup = async (title: string, confirmButtonText: string, cancelButtonText: string) => {
    return Swal.fire({
        title,
        icon: 'question',
        background: 'var(--alert-popup-bg)',
        color: 'var(--black-text)',
        showCancelButton: true,
        confirmButtonColor: 'var(--primary)',
        cancelButtonColor: 'var(--reset-button-bg)',
        confirmButtonText,
        cancelButtonText,
        allowOutsideClick: false
    });
}

export const confirmationPopupWithText = async (
    title: string,
    text: string,
    confirmButtonText: string,
    cancelButtonText: string
) => {
    return Swal.fire({
        title,
        text,
        icon: 'warning',
        background: 'var(--alert-popup-bg)',
        color: 'var(--black-text)',
        showCancelButton: true,
        confirmButtonColor: 'var(--primary)',
        cancelButtonColor: 'var(--reset-button-bg)',
        confirmButtonText,
        cancelButtonText,
        allowOutsideClick: false,
    });
}

export const infoPopup = async (title: string = infoMessages.featureNotAvailable, text?: string) => {
    return Swal.fire({
        title,
        icon: 'info',
        background: 'var(--alert-popup-bg)',
        color: 'var(--black-text)',
        confirmButtonColor: 'var(--primary)',
        confirmButtonText: 'Ok',
    });
}

export const errorPopup = async (title: string, text?: string) => {
    return Swal.fire({
        title,
        text,
        icon: 'error',
        background: 'var(--alert-popup-bg)',
        color: 'var(--black-text)',
        confirmButtonColor: 'var(--primary)',
        confirmButtonText: 'Ok',
    });
}

/** Enrollment API failure response shape */
export interface EnrollmentFailureDetail {
    student_id?: number;
    reg_number?: string;
    course_id?: number;
    message?: string;
    [key: string]: unknown;
}

export interface EnrollmentFailureResponse {
    message?: string;
    response?: {
        credit_policy_failures?: EnrollmentFailureDetail[];
        prerequisite_failures?: EnrollmentFailureDetail[];
        already_enrolled?: EnrollmentFailureDetail[];
    };
}

/** Map of course_id to course code and title for display in failure popup */
export interface EnrollmentCourseDetailsMap {
    [courseId: number]: { course_code: string; course_title: string };
}

const escapeHtml = (s: unknown): string => {
    if (s == null) return "";
    const str = String(s);
    return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
};

const renderDetailItem = (
    item: EnrollmentFailureDetail,
    courseDetailsMap?: EnrollmentCourseDetailsMap
): string => {
    const parts: string[] = [];
    if (item.reg_number != null)
        parts.push(`<strong>Roll No.:</strong> ${escapeHtml(item.reg_number)}`);
    if (item.course_id != null) {
        const course = courseDetailsMap?.[item.course_id];
        if (course?.course_code != null || course?.course_title != null) {
            const label = [course.course_code, course.course_title].filter(Boolean).join(" — ");
            parts.push(`<strong>Course:</strong> ${escapeHtml(label)}`);
        } else {
            parts.push(`<strong>Course ID:</strong> ${escapeHtml(item.course_id)}`);
        }
    }
    if (item.message != null)
        parts.push(`<strong>Message:</strong> ${escapeHtml(item.message)}`);
    return parts.length ? `<li class="enrollment-failure-item">${parts.join(" — ")}</li>` : "";
};

/**
 * Shows an error popup with detailed enrollment failure info (credit policy, prerequisite, already enrolled).
 * Call when POST /api/admin/students/enrollment returns status: false with response arrays.
 * @param courseDetailsMap Optional map of course_id to { course_code, course_title } to show course code/title instead of ID.
 */
export const enrollmentFailureDetailsPopup = async (
    apiResponse: EnrollmentFailureResponse,
    courseDetailsMap?: EnrollmentCourseDetailsMap
) => {
    const res = apiResponse?.response ?? {};
    const creditPolicy = res.credit_policy_failures ?? [];
    const prerequisite = res.prerequisite_failures ?? [];
    const alreadyEnrolled = res.already_enrolled ?? [];
    const hasAny = creditPolicy.length > 0 || prerequisite.length > 0 || alreadyEnrolled.length > 0;

    const render = (item: EnrollmentFailureDetail) => renderDetailItem(item, courseDetailsMap);

    const sections: string[] = [];
    if (creditPolicy.length > 0) {
        const items = creditPolicy.map(render).join("");
        sections.push(
            `<div class="enrollment-failure-section"><h4 class="enrollment-failure-heading">Credit policy failures</h4><ul class="enrollment-failure-list">${items}</ul></div>`
        );
    }
    if (prerequisite.length > 0) {
        const items = prerequisite.map(render).join("");
        sections.push(
            `<div class="enrollment-failure-section"><h4 class="enrollment-failure-heading">Prerequisite failures</h4><ul class="enrollment-failure-list">${items}</ul></div>`
        );
    }
    if (alreadyEnrolled.length > 0) {
        const items = alreadyEnrolled.map(render).join("");
        sections.push(
            `<div class="enrollment-failure-section"><h4 class="enrollment-failure-heading">Already enrolled</h4><ul class="enrollment-failure-list">${items}</ul></div>`
        );
    }

    const htmlContent = hasAny
        ? `<div class="enrollment-failure-details">${sections.join("")}</div>`
        : `<p>${escapeHtml(apiResponse?.message ?? "Enrollment failed.")}</p>`;

    const inlineStyles = `
      <style>
        .enrollment-failure-popup .enrollment-failure-intro { margin-bottom: 1rem; }
        .enrollment-failure-popup .enrollment-failure-details { max-height: 60vh; overflow-y: auto; text-align: left; }
        .enrollment-failure-popup .enrollment-failure-section { margin-bottom: 1rem; }
        .enrollment-failure-popup .enrollment-failure-heading { margin: 0 0 0.5rem 0; font-size: 0.95rem; color: inherit; }
        .enrollment-failure-popup .enrollment-failure-list { margin: 0; padding-left: 1.25rem; }
        .enrollment-failure-popup .enrollment-failure-item { margin-bottom: 0.35rem; }
      </style>
    `;

    return Swal.fire({
        title: "Enrollment Failed",
        html: `${inlineStyles}<p class="enrollment-failure-intro">${escapeHtml(apiResponse?.message ?? "Enrollment failed for all students. See details per student below.")}</p>${htmlContent}`,
        icon: "error",
        background: "var(--alert-popup-bg)",
        color: "var(--black-text)",
        confirmButtonColor: "var(--primary)",
        confirmButtonText: "OK",
        customClass: {
            popup: "enrollment-failure-popup",
        },
    });
}